import { adminService, AdminUser } from '@/services/admin.service';
import { useAuthStore } from '@/store/auth.store';
import { startImpersonation } from '@/utils/impersonation';
import { LogIn, Search, ShieldCheck } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

export function AdminUsersPage() {
  const role = useAuthStore(s => s.user?.role);
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    adminService
      .getUsers()
      .then(data => {
        if (active) {
          setUsers(data);
          setError(null);
        }
      })
      .catch(e => {
        if (active)
          setError(
            e?.response?.data?.message || e?.message || 'Erreur de chargement'
          );
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return users;
    return users.filter(
      u =>
        u.email?.toLowerCase().includes(q) ||
        `${u.firstName ?? ''} ${u.lastName ?? ''}`.toLowerCase().includes(q) ||
        u.role?.toLowerCase().includes(q)
    );
  }, [users, search]);

  if (role !== 'admin') {
    return (
      <div className="mx-auto max-w-2xl px-4 py-16 text-center text-neutral-500">
        Accès réservé aux administrateurs.
      </div>
    );
  }

  const handleImpersonate = async (u: AdminUser) => {
    if (!u.isActive) return;
    try {
      setBusy(u.uuid);
      const res = await adminService.impersonate(u.uuid);
      startImpersonation(u, res.accessToken, res.expiresIn);
    } catch (cause) {
      const e = cause as HttpApiError;
      setBusy(null);
      alert(
        e?.response?.data?.message ||
          e?.message ||
          'Impossible de se connecter en tant que cet utilisateur'
      );
    }
  };

  return (
    <div className="mx-auto max-w-6xl px-4 py-8">
      <div className="mb-6 flex items-center gap-3">
        <ShieldCheck className="text-brand-600 h-7 w-7" />
        <div>
          <h1 className="text-2xl font-bold text-neutral-900">
            Administration — Utilisateurs
          </h1>
          <p className="text-sm text-neutral-500">
            Tous les comptes. « Se connecter en tant que » ouvre l'application
            avec les accès exacts de la personne (dossiers, coffres…).
          </p>
        </div>
      </div>

      <div className="relative mb-4 max-w-md">
        <Search className="pointer-events-none absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-neutral-400" />
        <input
          type="text"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Rechercher par nom, email ou rôle…"
          className="focus:border-brand-500 focus:ring-brand-500 w-full rounded-lg border border-neutral-300 py-2 pr-3 pl-9 text-sm focus:ring-1 focus:outline-none"
        />
      </div>

      {loading && <p className="text-sm text-neutral-500">Chargement…</p>}
      {error && <p className="text-sm text-red-600">Erreur : {error}</p>}

      {!loading && !error && (
        <div className="overflow-x-auto rounded-xl border border-neutral-200 bg-white">
          <table className="min-w-full divide-y divide-neutral-200 text-sm">
            <thead className="bg-neutral-50 text-left text-xs tracking-wide text-neutral-500 uppercase">
              <tr>
                <th className="px-4 py-3">Nom</th>
                <th className="px-4 py-3">Email</th>
                <th className="px-4 py-3">Rôle</th>
                <th className="px-4 py-3">Statut</th>
                <th className="px-4 py-3 text-right">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {filtered.map(u => (
                <tr key={u.uuid} className="hover:bg-neutral-50">
                  <td className="px-4 py-3 font-medium text-neutral-900">
                    {`${u.firstName ?? ''} ${u.lastName ?? ''}`.trim() || '—'}
                  </td>
                  <td className="px-4 py-3 text-neutral-600">{u.email}</td>
                  <td className="px-4 py-3">
                    <span className="rounded-full bg-neutral-100 px-2 py-0.5 text-xs font-medium text-neutral-700">
                      {u.role}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    {u.isActive ? (
                      <span className="text-xs font-medium text-green-600">
                        Actif
                      </span>
                    ) : (
                      <span className="text-xs font-medium text-neutral-400">
                        Inactif
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      type="button"
                      disabled={!u.isActive || busy === u.uuid}
                      onClick={() => handleImpersonate(u)}
                      className="bg-brand-600 hover:bg-brand-500 inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold text-white transition-colors disabled:cursor-not-allowed disabled:opacity-40"
                    >
                      <LogIn className="h-3.5 w-3.5" />
                      {busy === u.uuid ? '…' : 'Se connecter en tant que'}
                    </button>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr>
                  <td
                    colSpan={5}
                    className="px-4 py-8 text-center text-neutral-400"
                  >
                    Aucun utilisateur
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
